import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { User, Hash, ArrowRight, Loader2, Sparkles, Clock } from 'lucide-react';
import type { UnifiedSearchResponse } from '../types';
import { StarBadge } from '../../../components/shared/star-badge';
import { ChannelMediaImage } from '../../../components/channel-media-image';

interface TypeaheadDropdownProps {
    query: string;
    debouncedQuery: string;
    data: UnifiedSearchResponse | undefined;
    isLoading: boolean;
    searchHistory: string[];
    selectedIndex: number;
    onClose: () => void;
    onViewAll: () => void;
    onResultSelect: (path: string) => void;
    onHistorySelect: (query: string) => void;
}

export function TypeaheadDropdown({
    query,
    debouncedQuery,
    data,
    isLoading,
    searchHistory,
    selectedIndex,
    onViewAll,
    onResultSelect,
    onHistorySelect,
}: TypeaheadDropdownProps) {
    const { t } = useTranslation();

    const hasResults = data && (data.pages.length > 0 || data.tags.length > 0);
    const queryTooShort = query.length < 3;
    const showHistory = queryTooShort && searchHistory.length > 0;

    return (
        <div className="typeahead-dropdown" role="listbox" aria-label={t('unifiedSearch.results')}>
            {/* Search History */}
            {showHistory && (
                <div className="typeahead-section">
                    <div className="typeahead-section-header">
                        <Clock className="h-3.5 w-3.5" />
                        <span>{t('unifiedSearch.recentSearches', 'Recent Searches')}</span>
                    </div>
                    {searchHistory.map((historyItem, idx) => (
                        <button
                            key={idx}
                            className={`typeahead-item w-full text-left ${selectedIndex === idx ? 'bg-accent' : ''}`}
                            onClick={() => onHistorySelect(historyItem)}
                        >
                            <div className="typeahead-item-tag-icon">
                                <Clock className="h-4 w-4" />
                            </div>
                            <div className="typeahead-item-content">
                                <span className="typeahead-item-name">{historyItem}</span>
                            </div>
                        </button>
                    ))}
                </div>
            )}

            {/* Too short hint */}
            {queryTooShort && !showHistory && (
                <div className="typeahead-hint">
                    <Sparkles className="h-4 w-4 text-primary/60" />
                    <span>{t('unifiedSearch.minCharsHint')}</span>
                </div>
            )}

            {/* Loading state */}
            {!queryTooShort && isLoading && (
                <div className="typeahead-loading">
                    <Loader2 className="h-4 w-4 animate-spin text-primary" />
                    <span>{t('unifiedSearch.searching')}</span>
                </div>
            )}

            {/* No results */}
            {!queryTooShort && !isLoading && !hasResults && debouncedQuery.length >= 3 && (
                <div className="typeahead-empty">
                    <span>{t('unifiedSearch.noResults')}</span>
                    <span className="typeahead-empty-sub">{t('unifiedSearch.tryDifferent')}</span>
                </div>
            )}

            {/* Pages section */}
            {data && data.pages.length > 0 && (
                <div className="typeahead-section">
                    <div className="typeahead-section-header">
                        <User className="h-3.5 w-3.5" />
                        <span>{t('unifiedSearch.memorialPages')}</span>
                        <span className="typeahead-count">{data.total_pages}</span>
                    </div>
                    {data.pages.map((page, idx) => {
                        const isSelected = selectedIndex === idx;
                        return (
                            <Link
                                key={page.page_id}
                                to={`/memorial/${page.slug || page.page_id}`}
                                className={`typeahead-item ${isSelected ? 'bg-accent' : ''}`}
                                onClick={(e) => {
                                    e.preventDefault();
                                    onResultSelect(`/memorial/${page.slug || page.page_id}`);
                                }}
                            >
                                <div className="relative flex-shrink-0">
                                    <div className="typeahead-item-avatar">
                                        {page.image_url ? (
                                            <img
                                                src={page.image_url}
                                                alt={page.fullname}
                                                className="h-full w-full object-cover"
                                            />
                                        ) : (
                                            <span className="typeahead-item-avatar-fallback">
                                                {page.fullname[0]?.toUpperCase()}
                                            </span>
                                        )}
                                    </div>
                                    <StarBadge starColor={page.star_color} size="xs" />
                                </div>
                                <div className="typeahead-item-content">
                                    <span className="typeahead-item-name">{page.fullname}</span>
                                    <div className="typeahead-item-meta">
                                        {page.country_name && <span>{page.country_name}</span>}
                                        {page.place_of_birth && (
                                            <>
                                                <span className="typeahead-item-separator">·</span>
                                                <span>{page.place_of_birth}</span>
                                            </>
                                        )}
                                    </div>
                                </div>
                                {page.is_private && (
                                    <span className="typeahead-privacy-badge">
                                        <svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                                        </svg>
                                    </span>
                                )}
                            </Link>
                        );
                    })}
                </div>
            )}

            {/* Tags section */}
            {data && data.tags.length > 0 && (
                <div className="typeahead-section">
                    <div className="typeahead-section-header">
                        <Hash className="h-3.5 w-3.5" />
                        <span>{t('unifiedSearch.aTags')}</span>
                        <span className="typeahead-count">{data.total_tags}</span>
                    </div>
                    {data.tags.map((tag, idx) => {
                        const globalIndex = (data?.pages?.length || 0) + idx;
                        const isSelected = selectedIndex === globalIndex;
                        return (
                            <Link
                                key={tag.tag_id}
                                to={`/atag/${tag.slug || tag.tag_id}`}
                                className={`typeahead-item ${isSelected ? 'bg-accent' : ''}`}
                                onClick={(e) => {
                                    e.preventDefault();
                                    onResultSelect(`/atag/${tag.slug || tag.tag_id}`);
                                }}
                            >
                                <div className={`typeahead-item-tag-icon ${tag.cover_thumb_url ? 'overflow-hidden p-0' : ''}`}>
                                    {tag.cover_thumb_url ? (
                                        <ChannelMediaImage
                                            value={tag.cover_thumb_url}
                                            alt=""
                                            className="h-full w-full object-cover"
                                            loading="lazy"
                                        />
                                    ) : (
                                        <Hash className="h-4 w-4" />
                                    )}
                                </div>
                                <div className="typeahead-item-content">
                                    <span className="typeahead-item-name">{tag.display_name || tag.tag_name}</span>
                                    <span className="text-[10px] text-muted-foreground font-mono">@{tag.tag_name}</span>
                                    <div className="typeahead-item-meta">
                                        {tag.no_tagged_pages != null && (
                                            <span>{tag.no_tagged_pages} {t('unifiedSearch.linkedPages')}</span>
                                        )}
                                        {tag.access_type === 'managed' && (
                                            <>
                                                <span className="typeahead-item-separator">·</span>
                                                <span className="typeahead-managed-badge">{t('unifiedSearch.managed')}</span>
                                            </>
                                        )}
                                    </div>
                                </div>
                            </Link>
                        );
                    })}
                </div>
            )}

            {/* View all results CTA */}
            {hasResults && (
                <button
                    className="typeahead-view-all"
                    onClick={onViewAll}
                >
                    <span>{t('unifiedSearch.viewAllResults')}</span>
                    <ArrowRight className="h-4 w-4" />
                </button>
            )}
        </div>
    );
}
